/**
 * State selectors — pure-functie projecties van `ChatState` naar UI-ready
 * waarden. Single source of truth voor view-routing zodat header, body
 * en footer niet uit hun eigen if-bomen kunnen drift'en.
 */

import type { ContractDetailed } from '../api/contracts';
import type { ChannelId, ChatState } from './types';

/**
 * Welk hoofdscherm (body) zou er moeten worden gerenderd? De UI hangt
 * meerdere render-beslissingen op aan deze enum: header-mode, of de
 * footer een composer rendert, of de back-button in de header zichtbaar is.
 */
export type PanelView =
  | 'bootstrap' // service-status nog niet bekend
  | 'channel-picker' // multi-channel + geen pick + geen sessie
  | 'external-channel' // pick = whatsapp/messenger
  | 'identifying' // identity-check loopt — D3-skeleton afhankelijk van pick
  | 'auth-required' // pick = repair-request + identity = anonymous
  | 'service-offline' // serviceStatus.isAcceptingConversationRequests = false
  | 'intake-form' // single-channel of webchat-pick + nog geen bot-flow
  | 'assistant-flow' // bot vraagt naam/e-mail
  | 'queued-or-requesting' // sessie in aanmaak / wachten op operator
  | 'active-or-ended'; // operator aangesloten of gesprek afgelopen

export type PanelLayout = {
  view: PanelView;
  /** Of de bezoeker in een lopende of afgesloten chat-sessie zit. */
  inExistingSession: boolean;
  /** Channels (in volgorde) volgens config — minimaal `['webchat']`. */
  channels: ChannelId[];
  /** Of er een terug-pijl in de header moet staan. */
  showBackButton: boolean;
};

/**
 * Bepaalt de panel-view-state op basis van app-state + configuratie.
 * Alle render-beslissingen zouden hieruit moeten volgen zodat header,
 * body en footer altijd consistent zijn.
 *
 * Authenticatie-routing leest uit `state.auth` (gevuld door de
 * `IDENTITY_RESOLVED_*`-acties in de controller). De host-page hoeft niets
 * meer expliciet door te geven — de chat vraagt het zelf aan de portal
 * via `identityUrl` op het moment van channel-pick (B2-strategie).
 */
export function derivePanelLayout(
  state: ChatState,
  channels: ChannelId[],
): PanelLayout {
  const pickedChannel = state.ui.pickedChannel;
  const inExistingSession = state.session.phase !== 'none';
  const view = derivePanelView(
    state,
    pickedChannel,
    channels,
    inExistingSession,
  );
  // Tijdens de korte identity-check (`phase: 'identifying'`) hebben we
  // technisch een 'inExistingSession' (phase !== 'none'), maar visueel
  // moet de back-button gewoon werken zoals vóór de pick — bezoeker is
  // nog niets begonnen. Behandel het als geen bestaande sessie voor de
  // navigatie-knoppen.
  const treatAsInSession =
    inExistingSession && state.session.phase !== 'identifying';
  const showBackButton =
    channels.length > 1 && (pickedChannel !== null || treatAsInSession);
  return { view, inExistingSession, channels, showBackButton };
}

/**
 * Resolved kanaal-configuratie: leest uit `window.chatConfig.channels`
 * met fallback naar single-channel webchat. Reden om dit als selector
 * te hebben i.p.v. inline `window.chatConfig?.channels` overal: één
 * plek waar de fallback-logica leeft, eenvoudig te mocken in tests.
 */
export function resolveChannels(
  configured: readonly ChannelId[] | undefined,
): ChannelId[] {
  if (configured && configured.length > 0) return [...configured];
  return ['webchat'];
}

/**
 * Bij single-channel-config is er geen picker-UI, dus de controller
 * pickt het enige kanaal automatisch op bootstrap. Geeft `null` terug
 * voor multi-channel — controller doet dan niets en de picker draait
 * de show.
 */
export function resolveDefaultChannel(channels: ChannelId[]): ChannelId | null {
  return channels.length === 1 ? channels[0]! : null;
}

/**
 * Of de bezoeker bestanden mag staged voor verzending. Vereist een
 * actieve/queued webchat-sessie — externe kanalen (whatsapp/messenger)
 * staan niet onder onze controle.
 */
export function canAcceptFiles(state: ChatState): boolean {
  // Alléén de AVAA-intake-flow accepteert bijlagen tijdens 'aava-asking' —
  // bezoeker kan bv. een foto van de defect meesturen die de AI-assistent
  // direct in z'n analyse gebruikt (dedicated /message-endpoint met base64).
  //
  // De klassieke chat met een medewerker heeft géén upload-endpoint, dus
  // daar bieden we bestand-toevoegen niet aan — noch via de knop, noch via
  // drag-drop/dropzone (WI 164914 / AC3 — niet geïmplementeerd).
  if (state.assistant?.step === 'aava-asking') {
    return state.aava.status !== 'starting' && state.aava.status !== 'sending';
  }
  return false;
}

/**
 * Of er voldoende content is om een transcript-download zinvol te maken.
 * Voorkomt de "lege transcript"-knop in de bot-flow vóór de bezoeker
 * iets heeft getypt; tijdens queued/active/ended is er per definitie
 * al iets om te bewaren.
 */
export function canDownloadTranscript(state: ChatState): boolean {
  return state.assistantMessages.length + state.serverMessages.length > 0;
}

/**
 * Het volledige `ContractDetailed`-object dat de bezoeker heeft gekozen
 * in de AVAA-flow, of `null` zolang er nog geen selectie is. Combineert
 * `selectedId` + `items.find()` in één call — alle plekken die het
 * gekozen contract willen weten gaan via deze selector zodat we niet
 * elke keer dezelfde find-logica dupliceren.
 *
 * Bron-of-waarheid: zodra een contract gekozen is staat `selectedId` op
 * zijn Guid; het bijbehorende `ContractDetailed`-object staat in
 * `items`. Mocht een latere fetch de items wijzigen waardoor `selectedId`
 * stale wordt, dan zorgt de reducer (CONTRACTS_FETCH_SUCCEEDED) ervoor
 * dat we `selectedId` op null zetten — deze selector valt dan dus stil
 * terug op `null` zonder een verlopen contract terug te geven.
 */
export function selectSelectedContract(
  state: ChatState,
): ContractDetailed | null {
  const id = state.contracts.selectedId;
  if (!id) return null;
  return state.contracts.items.find((c) => c.id === id) ?? null;
}

/**
 * ID van de eenheid die hoort bij het gekozen contract. Dit is wat de
 * AVAA-intake-API straks nodig heeft om een gesprek voor de juiste
 * woning op te zetten. Returns `null` wanneer:
 *   - geen contract geselecteerd
 *   - het gekozen contract geen `unit` heeft (defensief — Customers-API
 *     levert `unit: null` in zeldzame edge-cases zoals een vooraf-
 *     geannuleerd contract)
 */
export function selectSelectedUnitId(state: ChatState): string | null {
  return selectSelectedContract(state)?.unit?.id ?? null;
}

/**
 * Of het gesprek "staat": de bezoeker zit in de eigenlijke chat-weergave, niet
 * meer in een voorportaal (kanaalkeuze, intake-vragen, contract-keuze of
 * wachtrij). Bepaalt of de emoji- én bijlage-knop bruikbaar zijn (WI 164916
 * emoji, WI 164914 bijlage) — die horen pas thuis in een lopend gesprek.
 *
 *   - Operator-chat: een medewerker is aangesloten (`phase === 'active'`).
 *   - AAVA AI-intake: de bezoeker is ingelogd + heeft een contract gekozen en
 *     staat in `aava-asking` — hij kan direct z'n eerste bericht (incl. een
 *     foto van de storing) opstellen. Wachten tot de AI antwoordt zou juist
 *     de bijlage bij het éérste bericht blokkeren. Tijdens een in-flight
 *     AAVA-call is de composer sowieso al disabled (`aava.status`), dus de
 *     knoppen zijn dan alsnog niet klikbaar.
 */
export function selectChatEstablished(state: ChatState): boolean {
  if (state.session.phase === 'active') return true;
  return state.assistant?.step === 'aava-asking';
}

/**
 * Welke variant van de rating-card moet getoond worden, als überhaupt?
 *
 *   - `'hidden'` — geen rating-UI: niet ended, of geen operator-attach,
 *     of bezoeker dismist met "Niet nu"
 *   - `'idle'`   — rating-card met lege sterren + "Niet nu" / "Verstuur"
 *   - `'submitted'` — bedank-card met groene check
 *
 * Single source of truth zodat de UI nooit "Verstuur"-knop toont nadat
 * cross-tab sync de submission al heeft gemarkeerd.
 */
export type RatingUiVariant = 'hidden' | 'idle' | 'submitted';

export function selectRatingUi(state: ChatState): RatingUiVariant {
  if (state.session.phase !== 'ended') return 'hidden';
  const rating = state.session.rating;
  // Niet rateable (bezoeker stopte tijdens queue) of dismissed → niets tonen.
  if (rating === null || rating.status === 'dismissed') return 'hidden';
  return rating.status; // 'idle' | 'submitted'
}

/**
 * Of de "op zoek naar medewerker"-card getoond moet worden, in-stream
 * onder de assistant-zin "Ik zoek nu een medewerker voor je…".
 *
 * `'searching'` is het hele venster tussen intake-submit en operator-attach:
 *   - assistant-flow `step === 'searching'` (request loopt nog niet bij server)
 *   - session.phase `'requesting'` (server-call uitstaand, captcha-validatie etc.)
 *   - session.phase `'queued'` (server heeft 'm aangenomen, wacht op operator)
 *
 * Zodra `phase === 'active'` (operator attached) → `'hidden'`.
 */
export type SearchingUiVariant = 'hidden' | 'searching';

export function selectSearchingUi(state: ChatState): SearchingUiVariant {
  if (state.assistant?.step === 'searching') return 'searching';
  if (state.session.phase === 'requesting') return 'searching';
  if (state.session.phase === 'queued') return 'searching';
  return 'hidden';
}

/**
 * Aantal ongelezen operator-berichten voor de launcher-badge. Definitie:
 *
 *   - Paneel open ⇒ altijd 0. Wat zichtbaar is ís per definitie gelezen;
 *     `lastReadAt` wordt door de reducer pas geüpdatet zodra de bezoeker
 *     het paneel verlaat. Daarom kunnen we niet zomaar `timestamp >
 *     lastReadAt` evalueren als het paneel openstaat — alle nieuwe
 *     operator-berichten zouden dan ten onrechte als ongelezen tellen.
 *   - Paneel dicht ⇒ tel operator-berichten met `timestamp > lastReadAt`.
 *
 * Customer/system-berichten worden niet meegeteld (onze eigen + grijze
 * pillen zijn nooit "ongelezen"-relevant voor de bezoeker).
 */
export function selectUnreadOperatorCount(state: ChatState): number {
  if (state.ui.panelOpen) return 0;
  let count = 0;
  for (const m of state.serverMessages) {
    if (m.author === 'operator' && m.timestamp > state.ui.lastReadAt) {
      count++;
    }
  }
  return count;
}

function derivePanelView(
  state: ChatState,
  pickedChannel: ChannelId | null,
  channels: ChannelId[],
  inExistingSession: boolean,
): PanelView {
  if (state.serviceStatus === null) return 'bootstrap';

  // Identity-check loopt (B2: lazy bij channel-pick). Toont een D3-
  // skeleton-view die qua header en body al lijkt op de bestemming —
  // intake-form-skeleton voor `webchat`, auth-required-skeleton voor
  // `repair-request`. Resolutie via de controller (50–500ms) swapt
  // naar de echte view.
  // 'identifying' alleen tonen wanneer er nog GEEN assistant-flow loopt.
  // Bij AAVA-session-restore wordt phase tijdelijk 'identifying' gezet
  // terwijl assistant.step al 'aava-asking' is — daar willen we de
  // assistant-flow zien, geen skeleton.
  if (state.session.phase === 'identifying' && state.assistant === null) {
    return 'identifying';
  }

  // Multi-channel zonder gekozen kanaal én zonder lopende sessie:
  // bezoeker moet eerst kiezen.
  if (
    channels.length > 1 &&
    pickedChannel === null &&
    !inExistingSession
  ) {
    return 'channel-picker';
  }

  // Repair-request: vereist authenticatie. Toon de muur alleen wanneer
  // de identity-check daadwerkelijk is uitgevoerd én geen JWT terugkwam.
  // `auth: 'unknown'` betekent: nog niet gevraagd — controller dispatcht
  // dan IDENTITY_CHECK_STARTED, wat ons in 'identifying'-pad zet.
  if (
    pickedChannel === 'repair-request' &&
    !inExistingSession &&
    state.auth === 'anonymous'
  ) {
    return 'auth-required';
  }

  // External-channel-context blijft staan ook als de chat-service offline
  // is (WhatsApp/Messenger zijn niet afhankelijk van onze server).
  if (
    pickedChannel !== null &&
    pickedChannel !== 'webchat' &&
    pickedChannel !== 'repair-request' &&
    !inExistingSession
  ) {
    return 'external-channel';
  }

  // Service offline overrulet de webchat-flow, maar NIET repair-request:
  // dat kanaal gebruikt Customers + AAVA APIs (eigen status, los van de
  // Chat-API). Bij `repair-request`-pick met `auth: 'anonymous'` zijn we
  // hierboven al naar 'auth-required' afgetakt; reachen we hier dan is
  // de bezoeker authenticated. External-channel is ook hierboven al
  // afgehandeld. Een al-lopende sessie blijft staan — daar valt de
  // polling-state vanzelf in 'ended' om.
  if (
    pickedChannel !== 'repair-request' &&
    !state.serviceStatus.isAcceptingConversationRequests &&
    !inExistingSession
  ) {
    return 'service-offline';
  }

  // Lopende sessie: queued/requesting krijgt eigen status-hero, active +
  // ended renderen de message-list.
  if (state.session.phase === 'requesting' || state.session.phase === 'queued') {
    return 'queued-or-requesting';
  }
  if (state.session.phase === 'active' || state.session.phase === 'ended') {
    return 'active-or-ended';
  }

  // Bot-flow loopt — assistent vraagt naam, e-mail, of wacht op een
  // parallelle identity-check (POC conversational webchat: bezoeker
  // diende zijn vraag in terwijl `reactToChannelPickIdentityCheck` nog
  // bezig was → session.phase blijft `'identifying'` tot het portal
  // antwoordt, maar assistant.step is dan al `'awaiting-identity'`).
  // `'error'` valt hier óók onder: bij een mislukte session-request houdt
  // de reducer de assistant-stream aan zodat de bezoeker zijn context
  // niet verliest (system-pill + composer enabled voor retry).
  if (
    state.assistant !== null &&
    (state.session.phase === 'none' ||
      state.session.phase === 'identifying' ||
      state.session.phase === 'error')
  ) {
    return 'assistant-flow';
  }

  // Default: intake-form (vraag-+-captcha).
  return 'intake-form';
}
